import type { LucideIcon } from "lucide-react"
import { Check, Hand, Mic, PenLine, TriangleAlert, Volume2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { robinAvatarFrameStyle, robinAvatarSrc } from "./utils/robin-avatar-frame"
import type { RobinState } from "./utils/robin-state"

/**
 * Robin's presence — one mark, eight states. The base frame (public/robin-states)
 * carries the ambient mood (rings, waveform, blink, expression) and cold-reads
 * the state; the discrete indicator is a larger badge that floats *outside* the
 * avatar so it reads at small sizes. Changing state fades to the new frame and
 * swaps the badge; active states get a soft breathing motion.
 *
 * The mark sits straight on the surface with no backing tile. The host box is
 * sized to Robin himself; the SVG is padded around him so the state rings can
 * spill past the box instead of being clipped (see robin-avatar-frame.ts).
 */
const BREATHING: RobinState[] = ["listening", "thinking", "responding", "speaking"]

type Badge = { Icon?: LucideIcon; dots?: boolean; cls: string }

/**
 * The floating corner badge per state. Idle has none — Robin is just present.
 * Thinking uses a little thought-bubble of "…" dots rather than a loader, so it
 * reads as Robin mulling it over, not the system spinning.
 */
const BADGES: Partial<Record<RobinState, Badge>> = {
  listening: { Icon: Mic, cls: "bg-primary text-primary-foreground" },
  thinking: { dots: true, cls: "bg-primary text-primary-foreground" },
  responding: { Icon: PenLine, cls: "bg-primary text-primary-foreground" },
  waiting: { Icon: Hand, cls: "bg-attention text-chrome" },
  speaking: { Icon: Volume2, cls: "bg-primary text-primary-foreground" },
  error: { Icon: TriangleAlert, cls: "bg-destructive text-white" },
  done: { Icon: Check, cls: "bg-success text-chrome" },
}

export function RobinAvatar({
  state = "idle",
  className,
}: {
  state?: RobinState
  className?: string
}) {
  const badge = BADGES[state]
  return (
    <span className={cn("relative inline-flex h-8 w-8 shrink-0", className)}>
      <img
        key={state}
        src={robinAvatarSrc(state)}
        alt={`Robin — ${state}`}
        draggable={false}
        style={robinAvatarFrameStyle()}
        className={cn(
          "pointer-events-none absolute max-w-none select-none duration-500 animate-in fade-in-0",
          BREATHING.includes(state) &&
            "motion-safe:animate-[robin-breathe_2.4s_ease-in-out_infinite]",
        )}
      />
      {badge && (
        <span
          className={cn(
            "absolute -right-[10%] -top-[10%] flex h-1/2 w-1/2 items-center justify-center rounded-full shadow-sm ring-2 ring-background duration-300 animate-in fade-in-0 zoom-in-50",
            badge.cls,
          )}
        >
          {badge.dots ? (
            <ThinkingDots />
          ) : badge.Icon ? (
            <badge.Icon className="h-3/5 w-3/5" />
          ) : null}
        </span>
      )}
    </span>
  )
}

/** Three "…" dots blinking in sequence — Robin mulling it over. */
function ThinkingDots() {
  return (
    <span className="flex items-center gap-[12%]" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-[26%] w-[26%] min-h-[2.5px] min-w-[2.5px] rounded-full bg-current motion-safe:animate-[robin-dot-wave_1.2s_ease-in-out_infinite]"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  )
}
